// Continuous-motion growth chart - stock market / revenue graph feel.
// Starts lower-left, climbs upper-right. Multiple growth points. Always moving.
// NOT scroll-dependent. Soft green glow. SAP/Rajiv-Talreja founder-brand premium.
//
// Implementation:
//  - A wide SVG with a "track" group containing two copies of an upward-trending
//    jagged waveform side-by-side, animated infinitely via CSS keyframes.
//  - A glowing dot traces the path continuously using <animateMotion>.
//  - Vertical tick marks scroll alongside the chart for trader-chart authenticity.
export default function GrowthChartBg({ variant = "dark" }) {
  const isDark = variant === "dark";
  const stroke1 = isDark ? "#3B82F6" : "#0A2540";
  const stroke2 = isDark ? "#10B981" : "#00A86B";
  const gridStroke = isDark ? "rgba(255,255,255,0.05)" : "rgba(10,37,64,0.05)";
  const fId = `hopGlow-${variant}`;
  const gradId = `hopGrad-${variant}`;
  const fillId = `hopGradFill-${variant}`;
  const gridId = `hopGrid-${variant}`;

  // Upward-trending jagged waveform - designed to tile horizontally.
  // y-axis: lower = larger y (SVG). We jag up/down but the trend climbs.
  // Width per segment = 1600. We render two copies side-by-side (3200) and
  // translate the whole group -50% to loop seamlessly.
  const SEG = 1600;
  const TILE_PATH = (xOffset) =>
    `M${xOffset + 0},700 L${xOffset + 80},680 L${xOffset + 160},695 L${xOffset + 240},650 L${xOffset + 320},670 L${xOffset + 400},620 L${xOffset + 480},635 L${xOffset + 560},590 L${xOffset + 640},610 L${xOffset + 720},555 L${xOffset + 800},575 L${xOffset + 880},520 L${xOffset + 960},540 L${xOffset + 1040},485 L${xOffset + 1120},505 L${xOffset + 1200},445 L${xOffset + 1280},470 L${xOffset + 1360},410 L${xOffset + 1440},430 L${xOffset + 1520},365 L${xOffset + 1600},380`;

  // Single seamless looping path = tile1 + connector + tile2 (which mirrors
  // back to baseline so the seam matches). For continuous growth we instead
  // restart each tile from y=700 - the eye reads it as continuous upward cycles.
  const path1 = TILE_PATH(0);
  const path2 = TILE_PATH(SEG);

  // Trace dot path - single tile
  const tracePath = TILE_PATH(0);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Glows */}
      <div
        className="absolute -top-40 right-0 w-[700px] h-[700px] rounded-full"
        style={{
          background: isDark
            ? "radial-gradient(circle, rgba(16,185,129,0.16) 0%, rgba(0,0,0,0) 60%)"
            : "radial-gradient(circle, rgba(0,168,107,0.10) 0%, rgba(0,0,0,0) 60%)",
        }}
      />
      <div
        className="absolute -bottom-40 left-0 w-[820px] h-[820px] rounded-full"
        style={{
          background: isDark
            ? "radial-gradient(circle, rgba(59,130,246,0.12) 0%, rgba(0,0,0,0) 60%)"
            : "radial-gradient(circle, rgba(10,37,64,0.07) 0%, rgba(0,0,0,0) 60%)",
        }}
      />

      <svg
        viewBox="0 0 1600 800"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        style={{ opacity: isDark ? 0.55 : 0.32 }}
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={stroke1} />
            <stop offset="100%" stopColor={stroke2} />
          </linearGradient>
          <linearGradient id={fillId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={stroke2} stopOpacity="0.32" />
            <stop offset="100%" stopColor={stroke2} stopOpacity="0" />
          </linearGradient>
          <filter id={fId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <pattern id={gridId} width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M 100 0 L 0 0 0 100" fill="none" stroke={gridStroke} strokeWidth="1" />
          </pattern>
        </defs>

        <rect width="1600" height="800" fill={`url(#${gridId})`} />

        {/* Scrolling track - two tile copies side-by-side, marquee-loop forever */}
        <g
          style={{
            animation: "hopChartScroll 28s linear infinite",
            transformOrigin: "0 0",
          }}
        >
          {/* Glow underlayer for both tiles */}
          <path
            d={path1}
            fill="none"
            stroke={stroke2}
            strokeWidth="7"
            strokeLinecap="round"
            opacity="0.35"
            filter={`url(#${fId})`}
          />
          <path
            d={path2}
            fill="none"
            stroke={stroke2}
            strokeWidth="7"
            strokeLinecap="round"
            opacity="0.35"
            filter={`url(#${fId})`}
          />

          {/* Main path tile 1 */}
          <path
            d={path1}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Main path tile 2 */}
          <path
            d={path2}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Area fill (lighter) */}
          <path
            d={`${path1} L${SEG},800 L0,800 Z`}
            fill={`url(#${fillId})`}
            opacity="0.45"
          />
          <path
            d={`${path2} L${SEG * 2},800 L${SEG},800 Z`}
            fill={`url(#${fillId})`}
            opacity="0.45"
          />

          {/* Trader-style vertical ticks every 80 units */}
          {Array.from({ length: 40 }).map((_, i) => {
            const x = i * 80;
            const tall = i % 5 === 0;
            return (
              <line
                key={i}
                x1={x}
                x2={x}
                y1={tall ? 750 : 760}
                y2={770}
                stroke={isDark ? "rgba(255,255,255,0.12)" : "rgba(10,37,64,0.12)"}
                strokeWidth="1"
              />
            );
          })}

          {/* Milestone bumps along the curve (both tiles) */}
          {[
            [400, 620],
            [800, 575],
            [1200, 445],
            [1600 + 400, 620],
            [1600 + 800, 575],
            [1600 + 1200, 445],
          ].map(([cx, cy], i) => (
            <g key={i}>
              <circle
                cx={cx}
                cy={cy}
                r="9"
                fill={stroke2}
                opacity="0.18"
                style={{
                  animation: `hopPulse 2.6s ease-in-out ${i * 0.3}s infinite`,
                  transformOrigin: `${cx}px ${cy}px`,
                }}
              />
              <circle
                cx={cx}
                cy={cy}
                r="4"
                fill={stroke2}
                stroke="#ffffff"
                strokeWidth="1"
                style={{ filter: `drop-shadow(0 0 6px ${stroke2})` }}
              />
            </g>
          ))}
        </g>

        {/* Static trace dot that walks the curve once per cycle for extra liveliness */}
        <g style={{ filter: `drop-shadow(0 0 8px ${stroke2})` }}>
          <circle r="6" fill={stroke2} stroke="#ffffff" strokeWidth="1.5">
            <animateMotion
              dur="9s"
              repeatCount="indefinite"
              rotate="auto"
              path={tracePath}
            />
          </circle>
        </g>
      </svg>
    </div>
  );
}
